import React from 'react';
import { Lightbulb, ShieldCheck, CheckCircle2, ArrowRight } from 'lucide-react';
import { Language } from '../types';

interface ServicesSectionProps {
  currentLang: Language;
  onSelectServiceForQuote: (serviceTitle: string) => void;
}

export const ServicesSection: React.FC<ServicesSectionProps> = ({ currentLang }) => {
  return (
    <section id="expertises" className="py-16 bg-slate-50 dark:bg-slate-950 transition-colors">
      <div className="w-[90%] lg:w-[70%] max-w-none mx-auto px-6">
        <div className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            {currentLang === 'ar' ? 'ركائز ومجالات خبرتنا' : currentLang === 'en' ? 'Our Core Pillars of Expertise' : "Nos Piliers d'Expertise"}
          </h2>
          <div className="w-12 h-0.5 bg-slate-900 dark:bg-white" />
        </div>

        <div className="flex flex-col gap-4 mb-16">
          {/* Card 1 */}
          <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 rounded-sm">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 flex items-center justify-center rounded-sm mb-6">
              <Lightbulb className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {currentLang === 'ar' ? 'الاستشارات الاستراتيجية والتنظيمية' : currentLang === 'en' ? 'Strategic Consulting & Governance' : 'Conseil Stratégique'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {currentLang === 'ar' 
                ? 'مواءمة أنظمة المعلومات والعمليات التشغيلية مع الأهداف الاستراتيجية لتحقيق نمو مستدام وموثوق.'
                : currentLang === 'en' 
                ? 'Architecting robust, scalable strategies tailored for high-stakes environments, driving sustainable growth and market leadership.' 
                : "Alignement des systèmes d'information avec vos objectifs d'affaires pour une croissance durable."}
            </p>
            <a href="#contact" className="text-xs font-semibold text-slate-900 dark:text-white flex items-center gap-1 hover:underline">
              {currentLang === 'ar' ? 'اكتشف المزيد' : currentLang === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
            </a>
          </div>

          {/* Card 2 */}
          <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 rounded-sm">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 flex items-center justify-center rounded-sm mb-6">
              <ShieldCheck className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {currentLang === 'ar' ? 'تدقيق تكنولوجيا المعلومات والامتثال' : currentLang === 'en' ? 'IT Audit & Governance' : 'Audit IT'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {currentLang === 'ar'
                ? 'تقييم دقيق للبنى التحتية التكنولوجية والعمليات لضمان الأمان والمرونة والأداء العالي والامتثال التنظيمي.'
                : currentLang === 'en' 
                ? 'Rigorous evaluation of technological infrastructures to mitigate risks and align IT strategy with overarching business objectives.' 
                : 'Évaluation rigoureuse de vos infrastructures pour garantir sécurité, performance et conformité.'}
            </p>
            <a href="#contact" className="text-xs font-semibold text-slate-900 dark:text-white flex items-center gap-1 hover:underline">
              {currentLang === 'ar' ? 'اكتشف المزيد' : currentLang === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
            </a>
          </div>

          {/* Card 3 */}
          <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 rounded-sm">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 flex items-center justify-center rounded-sm mb-6">
              <CheckCircle2 className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {currentLang === 'ar' ? 'مرافقة واعتماد معايير الآيزو (ISO)' : currentLang === 'en' ? 'ISO Certification' : 'Support ISO'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {currentLang === 'ar'
                ? 'مرافقة شاملة ومتكاملة للحصول على شهادات الآيزو الدولية وتجديدها (ISO 27001, 9001, 14001, 45001).'
                : currentLang === 'en' 
                ? 'Comprehensive guidance through complex international standards ensuring meticulous compliance and operational superiority.' 
                : "Accompagnement de bout en bout pour l'obtention et le maintien de vos certifications ISO (27001, 9001)."}
            </p>
            <a href="#contact" className="text-xs font-semibold text-slate-900 dark:text-white flex items-center gap-1 hover:underline">
              {currentLang === 'ar' ? 'اكتشف المزيد' : currentLang === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
            </a>
          </div>
        </div>

        {/* Bottom CTA Box */}
        <div className="bg-slate-900 dark:bg-slate-800 p-10 text-center rounded-sm">
           <p className="text-xs font-semibold text-white/60 mb-2 uppercase tracking-widest">
             {currentLang === 'ar' ? 'يحظى بثقة كبرى الشركات' : currentLang === 'en' ? 'Trusted by Leading Organizations' : 'Ils nous font confiance'}
           </p>
           <div className="flex justify-center gap-6 text-xs text-white/40 font-mono tracking-widest mb-10">
             <span>SONATRACH</span>
             <span>GLOBALTECH</span>
             <span>PORT AUTH</span>
             <span>NEXUS</span>
           </div>
           
           <h3 className="text-2xl font-semibold text-white mb-6">
             {currentLang === 'ar' ? 'هل أنتم مستعدون لتطوير وتأمين مؤسستكم؟' : currentLang === 'en' ? 'Ready to elevate your IT and operations?' : 'Prêt à transformer votre organisation ?'}
           </h3>
           <a 
             href="#contact" 
             className="inline-block bg-white text-slate-900 px-6 py-3 text-xs font-semibold hover:bg-slate-200 transition-colors rounded-sm"
           >
             {currentLang === 'ar' ? 'حجز موعد / طلب استشارة' : currentLang === 'en' ? 'Get in Touch / Request Quote' : 'Prendre Rendez-vous'}
           </a>
        </div>
      </div>
    </section>
  );
};
